/**
 * Main layout: the menu and the page (CC redesign, brief 1; Nino approved
 * Design's sketch, 6 Oct).
 *
 * The menu's shape follows the screen width, so the page always gets the
 * room it needs and nothing is cut off:
 *   - 1100 px and wider: the full menu (248 px);
 *   - 768–1099 px (iPad, 1024): an icon rail (64 px), names on hover;
 *   - narrower (phone): a top bar with ☰ that opens the menu as a drawer.
 * ⌘K opens the command palette from anywhere. Every page's content is at
 * most 1,120 px wide, from the menu's edge (page standard, Nino approved
 * Design's sketch, 12 Oct).
 */
import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUpRight, Menu, User as UserIcon } from 'lucide-react';
import Sidebar, { type MenuShape } from './Sidebar';
import { getAllNavItems, titleForPath } from '@/lib/nav';
import CommandPalette, { type CommandItem } from './CommandPalette';
import { TOOL, TOOL_NAME, otherToolUrl } from '@/lib/tool';

const OTHER = TOOL === 'class-creator' ? 'command-centre' : 'class-creator';

interface LayoutProps {
  children: ReactNode;
}

function shapeFor(width: number): MenuShape {
  if (width >= 1100) return 'full';
  if (width >= 768) return 'rail';
  return 'drawer';
}

function useMenuShape(): MenuShape {
  const [shape, setShape] = useState<MenuShape>(() => shapeFor(typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setShape(shapeFor(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return shape;
}

export default function Layout({ children }: LayoutProps) {
  const shape = useMenuShape();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // A route change (or growing past phone width) closes the drawer.
  useEffect(() => { setDrawerOpen(false); }, [pathname, shape]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // ⌘K searches this tool's pages, plus one line to the other tool (the split, 27 Oct).
  const paletteItems: CommandItem[] = [
    ...getAllNavItems().map((it) => ({ id: it.id, label: it.label, group: it.group, icon: it.icon, href: it.href })),
    {
      id: `tool:${OTHER}`, label: `Go to the ${TOOL_NAME[OTHER]}`, group: 'Elsewhere', icon: ArrowUpRight,
      keywords: [TOOL_NAME[OTHER], 'switch', 'other tool'],
      onSelect: () => { window.open(otherToolUrl(OTHER), '_blank', 'noopener,noreferrer'); },
    },
  ];

  if (shape === 'drawer') {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-30 flex h-[52px] items-center gap-2.5 border-b border-border bg-background px-3.5">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open the menu"
            aria-expanded={drawerOpen}
            className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-border text-foreground"
          >
            <Menu className="h-[18px] w-[18px]" />
          </button>
          <b className="min-w-0 flex-1 truncate text-[15px] text-foreground">{titleForPath(pathname)}</b>
          <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-foreground" aria-hidden>
            <UserIcon className="h-4 w-4 text-background" />
          </span>
        </header>
        {drawerOpen && (
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setDrawerOpen(false)}>
            <div role="dialog" aria-modal="true" aria-label="Menu" className="h-full w-[280px] overflow-hidden rounded-r-2xl" onClick={(e) => e.stopPropagation()}>
              <Sidebar shape="full" fold onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        )}
        <main className="px-3.5 pb-8 pt-4">{children}</main>
        <CommandPalette items={paletteItems} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="fixed inset-y-0 left-0 z-20">
        <Sidebar shape={shape} />
      </div>
      <div style={{ paddingLeft: shape === 'rail' ? 64 : 248 }}>
        <main className="px-8 pb-10 pt-7"><div className="max-w-[1120px]">{children}</div></main>
      </div>
      <CommandPalette items={paletteItems} />
    </div>
  );
}
